import {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {clientActionsApi} from 'api';
import {Button, PredefinedNotifierType} from 'components';
import {ClientContext} from 'context';
import {useApiData, useClientSession} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {
    PRE_SESSION_FLOW_LIMITS, PreSessionFlowState, PreSessionFlowCompletion, PreSessionStepStatus, PreSessionStepType, TextChatContentType,
} from 'types';
import {sessionUtils} from 'utils';

import {AsyncRequestStep} from '../../PreSessionFlowWizard/steps/AsyncRequestStep';
import {FileUploadStep} from '../../PreSessionFlowWizard/steps/FileUploadStep';
import {ScreenshotStep} from '../../PreSessionFlowWizard/steps/ScreenshotStep';

import './styles.scss';

const T_KEYS = TRANSLATION_KEYS.CLIENT_IN_SESSION_PAGE.PRE_SESSION_FLOW;

const PRE_SESSION_FLOW_COMPLETED_KEY = 'completed';

/**
 * The current step's own control, docked between the conversation and the message input — so a photo or a
 * vendor frame is done without leaving the chat, and the thread above stays readable while it is open.
 *
 * Form steps are answered by talking and produce nothing here.
 */
export const usePreSessionStepOptions = () => {
    const {t} = useTranslation();
    const {session, textChatActivity, avatarActivity} = useClientSession();
    const {endLocalSession, onSubmitMessage, textChatInputDisabled} = useContext(ClientContext);
    const [ending, setEnding] = useState(false);
    const [sendingOption, setSendingOption] = useState(false);

    const flowState = useMemo<PreSessionFlowState | null>(() => {
        const activity = textChatActivity || avatarActivity;
        const data = activity?.data as {preSessionFlow?: PreSessionFlowState} | undefined;
        return data?.preSessionFlow ?? null;
    }, [textChatActivity, avatarActivity]);

    const currentStep = useMemo(
        () => flowState?.flowSnapshot.steps[flowState.currentStepIndex] ?? null,
        [flowState],
    );

    const currentState = useMemo(
        () => (currentStep ? flowState?.steps.find((s) => s.stepId === currentStep.id) ?? null : null),
        [flowState, currentStep],
    );

    const sessionId = session?.id;
    const participantId = useMemo(() => sessionUtils.getClientParticipant(session)?.id, [session]);

    const {send: skipStep, loading: skipping} = useApiData(
        clientActionsApi.skipPreSessionStep,
        {notificationType: PredefinedNotifierType.SaveError},
    );

    // The detail the chatbot is asking for right now: the first one of the form step not yet given.
    const pendingOptionField = useMemo(() => {
        if (currentStep?.type !== PreSessionStepType.Form || currentState?.status !== PreSessionStepStatus.InProgress) {
            return null;
        }
        const given = currentState.collected?.form || {};
        const field = currentStep.form?.fields?.find((f) => !given[f.id]);
        return field?.options?.length ? field : null;
    }, [currentStep, currentState]);

    // Sent as the client's own chat message, so the answer goes through the same reading and validation as typing it.
    const handleOptionSelect = useCallback(async (option: string) => {
        if (sendingOption || textChatInputDisabled) {
            return;
        }
        setSendingOption(true);
        try {
            await onSubmitMessage({content: option, contentType: TextChatContentType.Text});
        } finally {
            setSendingOption(false);
        }
    }, [onSubmitMessage, sendingOption, textChatInputDisabled]);

    const {send: confirmFlow, loading: confirming} = useApiData(clientActionsApi.confirmPreSessionFlow, {
        notificationType: PredefinedNotifierType.SaveError,
    });
    const handleConfirm = useCallback(() => {
        if (sessionId && participantId) {
            confirmFlow({sessionId, participantId});
        }
    }, [confirmFlow, sessionId, participantId]);

    const handleEndSession = useCallback(() => {
        setEnding(true);
        endLocalSession();
    }, [endLocalSession]);

    const handleSkip = useCallback(() => {
        if (!sessionId || !participantId || !currentStep) {
            return;
        }
        skipStep({sessionId, participantId}, {stepId: currentStep.id});
    }, [skipStep, sessionId, participantId, currentStep]);

    const skipButton = useMemo(() => {
        if (!currentStep || !currentState) {
            return null;
        }
        // Required async steps become skippable only after exhausting retries — same rule as the wizard,
        // so a verification that keeps failing cannot trap the client.
        const retriesExhausted = currentStep.type === PreSessionStepType.AsyncRequest
            && currentState.attempts >= PRE_SESSION_FLOW_LIMITS.PER_STEP_MAX_RETRIES;
        if (currentStep.required && !retriesExhausted) {
            return null;
        }
        return (
            <Button
                className='tw-max-w-40'
                color='default'
                icon='chevron-right'
                disabled={skipping}
                loading={skipping}
                onClick={handleSkip}
            >
                {t(T_KEYS.SKIP)}
            </Button>
        );
    }, [currentStep, currentState, skipping, handleSkip, t]);

    const panel = useMemo(() => {
        if (!currentStep || !sessionId || !participantId) {
            return null;
        }
        const stepProps = {sessionId, participantId, step: currentStep, skipButton};
        if (currentStep.type === PreSessionStepType.AsyncRequest) {
            return <AsyncRequestStep {...stepProps} />;
        }
        if (currentStep.type === PreSessionStepType.FileUpload) {
            return <FileUploadStep {...stepProps} />;
        }
        if (currentStep.type === PreSessionStepType.Screenshot) {
            return <ScreenshotStep {...stepProps} />;
        }
        return null;
    }, [currentStep, sessionId, participantId, skipButton]);

    return useMemo(() => {
        const isChatbotMode = !!flowState?.flowSnapshot.modes?.chatbot;
        const panelStyle = isChatbotMode && (textChatInputDisabled || sendingOption)
            ? {display: 'none'} : undefined;
        // Once a conversational flow is over the client may still have questions, so the chat stays open and
        // leaving is offered in the same place the step buttons were.
        if (isChatbotMode && flowState?.awaitingConfirmation) {
            const endsSession = [PreSessionFlowCompletion.End, PreSessionFlowCompletion.Chat]
                .includes(flowState.flowSnapshot.onComplete);
            return (
                <div key={PRE_SESSION_FLOW_COMPLETED_KEY} className='vs-pre-session-step-panel'>
                    <Button color='primary' disabled={confirming} loading={confirming} onClick={handleConfirm}>
                        {t(endsSession ? T_KEYS.END_SESSION : T_KEYS.CONNECT)}
                    </Button>
                </div>
            );
        }
        if (flowState?.completedInChat) {
            return (
                <div key={PRE_SESSION_FLOW_COMPLETED_KEY} className='vs-pre-session-step-panel' style={panelStyle}>
                    <Button color='default' icon='log-out' disabled={ending} loading={ending} onClick={handleEndSession}>
                        {t(T_KEYS.END_SESSION)}
                    </Button>
                </div>
            );
        }
        if (isChatbotMode && pendingOptionField) {
            return (
                <div key={pendingOptionField.id} className='vs-pre-session-step-panel' style={panelStyle}>
                    <div className='tw-flex tw-flex-wrap tw-justify-end tw-gap-2'>
                        {pendingOptionField.options.map((option) => (
                            <Button
                                key={option}
                                color='default'
                                disabled={sendingOption || textChatInputDisabled}
                                onClick={() => handleOptionSelect(option)}
                            >
                                {option}
                            </Button>
                        ))}
                    </div>
                </div>
            );
        }
        const isInProgress = currentState?.status === PreSessionStepStatus.InProgress;
        const needsControl = currentStep && currentStep.type !== PreSessionStepType.Form;
        if (!isChatbotMode || !isInProgress || !needsControl || !panel) {
            return null;
        }
        // Keyed by step so each new step mounts afresh and plays its entrance, instead of swapping contents in place.
        return <div key={currentStep.id} className='vs-pre-session-step-panel' style={panelStyle}>{panel}</div>;
    }, [flowState, currentState, currentStep, panel, ending, handleEndSession, t, pendingOptionField, sendingOption,
        handleOptionSelect, textChatInputDisabled, confirming, handleConfirm]);
};
